import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { CalendarDays, Lock, Plus, Trash2, Wrench } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { CorrectionForm, CorrectionList } from "@/components/PaymentCorrections";

const fmt = (n) => new Intl.NumberFormat("en-TZ", { maximumFractionDigits: 2 }).format(Number(n) || 0);
const today = () => new Date().toISOString().slice(0, 10);

// The dated installments behind an invoice's "amount paid" -- what the
// customer statement is built from. Installments created by Record Payment
// are locked (a real Sale + Deposit backs them). Historical ones -- paid
// before payment tracking existed -- can be dated and split into the real
// installments here, as long as they still add up to the same total
// (enforced again server-side: PUT /invoices/{id}/payment-history).
// Locked installments can't be edited here -- a mistake in one is fixed by
// a correction request that a SuperAdmin approves (PaymentCorrections.jsx).
export function PaymentHistory({ invoice, canEdit, onSaved }) {
  const { user } = useAuth();
  const payments = invoice.payments || [];
  const editable = payments.filter((p) => !p.locked);
  const editableTotal = editable.reduce((s, p) => s + (Number(p.amount) || 0), 0);
  const [rows, setRows] = useState(null); // null = viewing, array = editing
  const [saving, setSaving] = useState(false);
  const [corrections, setCorrections] = useState([]);
  const [correcting, setCorrecting] = useState(null); // the locked payment being corrected

  const loadCorrections = useCallback(() => {
    api.get(`/invoices/${invoice.id}/corrections`)
      .then(({ data }) => setCorrections(data.items || []))
      .catch(() => {});
  }, [invoice.id]);
  useEffect(() => { loadCorrections(); }, [loadCorrections, invoice.payments]);

  if (!payments.length && !corrections.length) return null;
  const pendingFor = (p) => corrections.some((c) => c.status === "pending" && c.payment_id === p.id);

  const startEdit = () => setRows(editable.map((p) => ({ ...p, amount: String(p.amount), date: p.date || "" })));
  const update = (i, patch) => setRows(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const allocated = (rows || []).reduce((s, r) => s + (Number(r.amount) || 0), 0);
  const remaining = Math.round((editableTotal - allocated) * 100) / 100;

  const save = async () => {
    if (rows.some((r) => !(Number(r.amount) > 0))) { toast.error("Every installment needs an amount greater than zero"); return; }
    if (Math.abs(remaining) > 0.005) { toast.error(`Installments must add up to TZS ${fmt(editableTotal)}`); return; }
    setSaving(true);
    try {
      const { data } = await api.put(`/invoices/${invoice.id}/payment-history`, {
        payments: rows.map((r) => ({
          date: r.date || null, amount: Number(r.amount), method: r.method || "",
          reference: r.reference || "", sale_no: r.sale_no || "", note: r.date ? "" : r.note || "",
        })),
      });
      toast.success("Payment history updated");
      setRows(null);
      onSaved?.(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="payment-history">
      <div className="flex items-center justify-between gap-2">
        <Label className="text-xs uppercase tracking-[0.16em]">Payment History</Label>
        {canEdit && editable.length > 0 && !rows && (
          <Button size="sm" variant="outline" onClick={startEdit} data-testid="edit-payment-history">
            <CalendarDays className="w-3.5 h-3.5 mr-1.5" /> Set dates / installments
          </Button>
        )}
      </div>

      {!rows ? (
        <div className="overflow-x-auto rounded-lg border border-border">
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/40 hover:bg-muted/40">
                <TableHead className="text-xs">Date Paid</TableHead>
                <TableHead className="text-xs text-right">Amount</TableHead>
                <TableHead className="text-xs">Method</TableHead>
                <TableHead className="text-xs">Reference</TableHead>
                {canEdit && <TableHead className="text-xs w-24" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p, i) => (
                <TableRow key={p.id || i}>
                  <TableCell className="text-sm whitespace-nowrap">
                    {p.date || <span className="text-amber-600">Not recorded</span>}
                    {p.locked && <Lock className="inline w-3 h-3 ml-1.5 text-muted-foreground" aria-label="Recorded via Record Payment" />}
                    {p.corrected && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-sky-600" title="Changed by an approved correction">corrected</span>}
                  </TableCell>
                  <TableCell className="text-sm text-right">{fmt(p.amount)}</TableCell>
                  <TableCell className="text-sm">{p.method || "—"}</TableCell>
                  <TableCell className="text-sm">{[p.reference, p.sale_no].filter(Boolean).join(" · ") || "—"}</TableCell>
                  {canEdit && (
                    <TableCell className="text-right">
                      {p.locked && (pendingFor(p)
                        ? <span className="text-xs text-amber-600">Correction pending</span>
                        : (
                          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => setCorrecting(p)} data-testid={`correct-payment-${p.id}`}>
                            <Wrench className="w-3 h-3 mr-1" /> Correct
                          </Button>
                        ))}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      ) : (
        <div className="space-y-2 rounded-lg border border-border p-3 bg-muted/20">
          <p className="text-xs text-muted-foreground">
            Split TZS {fmt(editableTotal)} into the installments actually received and set when each was paid.
            The total can't change here — use Record Payment for new money.
          </p>
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-2 sm:grid-cols-[9.5rem_8rem_1fr_1fr_auto] gap-2 items-center">
              <Input type="date" max={today()} value={r.date} onChange={(e) => update(i, { date: e.target.value })} aria-label="Date paid" data-testid={`installment-date-${i}`} />
              <MoneyInput value={r.amount} onChange={(e) => update(i, { amount: e.target.value })} aria-label="Amount" data-testid={`installment-amount-${i}`} />
              <Input value={r.method || ""} onChange={(e) => update(i, { method: e.target.value })} placeholder="Method (e.g. NMB Bank)" aria-label="Method" />
              <Input value={r.reference || ""} onChange={(e) => update(i, { reference: e.target.value })} placeholder="Reference" aria-label="Reference" />
              <Button type="button" size="icon" variant="ghost" disabled={rows.length === 1} onClick={() => setRows(rows.filter((_, j) => j !== i))} aria-label="Remove installment">
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <Button type="button" size="sm" variant="outline"
              onClick={() => setRows([...rows, { date: "", amount: remaining > 0 ? String(remaining) : "", method: rows[0]?.method || "", reference: "" }])}
              data-testid="add-installment">
              <Plus className="w-3.5 h-3.5 mr-1.5" /> Add installment
            </Button>
            <span className={`text-xs ${Math.abs(remaining) > 0.005 ? "text-amber-600" : "text-emerald-600"}`} data-testid="installments-remaining">
              {Math.abs(remaining) > 0.005 ? `Left to allocate: TZS ${fmt(remaining)}` : "Fully allocated ✓"}
            </span>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => setRows(null)}>Cancel</Button>
            <Button size="sm" onClick={save} disabled={saving || Math.abs(remaining) > 0.005} className="kn-gradient-bg text-white border-0" data-testid="save-payment-history">
              {saving ? "Saving…" : "Save"}
            </Button>
          </div>
        </div>
      )}

      {correcting && (
        <CorrectionForm
          key={correcting.id}
          invoiceId={invoice.id}
          payment={correcting}
          onCancel={() => setCorrecting(null)}
          onDone={() => { setCorrecting(null); loadCorrections(); }}
        />
      )}
      <CorrectionList
        corrections={corrections}
        isSuperAdmin={user?.role === "SuperAdmin"}
        onDecided={(updated) => { if (updated) onSaved?.(updated); loadCorrections(); }}
      />
    </div>
  );
}
